import { useEffect } from 'react'
import { faviconSrc } from './BrandAsset'

export const MAX_BROWSER_TITLE_LENGTH = 120
export const ONMARBLE_BROWSER_SUFFIX = ' | OnMarble'

/** The browser title is data, never markup. */
export function normaliseBrowserTitle(value: string | null | undefined): string | null {
  const title = value?.trim()
  if (!title || [...title].length > MAX_BROWSER_TITLE_LENGTH) return null
  if ([...title].some((character) => {
    const code = character.codePointAt(0) ?? 0
    return code <= 31 || (code >= 127 && code <= 159)
  })) return null
  return title
}

/**
 * Product hosts always carry the public platform identity. The configurable
 * part says whose workspace/portal this is; OnMarble says what it runs on.
 * Accept an already-composed value so importing older deployment data cannot
 * produce `| OnMarble | OnMarble`.
 */
export function onMarbleBrowserTitle(value: string | null | undefined): string | null {
  const title = normaliseBrowserTitle(value)
  if (!title) return null
  return title.endsWith(ONMARBLE_BROWSER_SUFFIX) ? title : `${title}${ONMARBLE_BROWSER_SUFFIX}`
}

export interface DocumentIdentityOptions {
  title?: string | null
  fallbackTitle: string
  favicon?: string | null
}

/**
 * Apply resolved brand data to the browser document and restore the host's
 * static identity when the brand changes or the host unmounts.
 */
export function useDocumentIdentity({ title, fallbackTitle, favicon }: DocumentIdentityOptions): void {
  useEffect(() => {
    const previousTitle = document.title
    const resolvedTitle = onMarbleBrowserTitle(title) ?? onMarbleBrowserTitle(fallbackTitle)
    if (resolvedTitle) document.title = resolvedTitle

    const previousLink = document.querySelector<HTMLLinkElement>('link[rel~="icon"]')
    const previousHref = previousLink?.getAttribute('href') ?? null
    const src = faviconSrc(favicon)
    let link = previousLink
    let created = false

    if (src) {
      if (!link) {
        link = document.createElement('link')
        link.rel = 'icon'
        link.dataset.solumDocumentIdentity = 'true'
        document.head.appendChild(link)
        created = true
      }
      link.href = src
    }

    return () => {
      document.title = previousTitle
      if (created) link?.remove()
      else if (link && previousHref !== null) link.setAttribute('href', previousHref)
      else link?.removeAttribute('href')
    }
  }, [favicon, fallbackTitle, title])
}
